import type { QueryClient } from "@tanstack/react-query";
import { PostHog } from "posthog-js";
import * as React from "react";
import { Outlet, redirect, RouteObject, unstable_MiddlewareFunction } from "react-router";
import { getParticipantRoutes } from "~/participant/participant.routes";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";
import { LoadingContainer } from "~/shared/components/ds/Spinner";
import { TopLevelRouteErrorHandler } from "~/shared/components/errors/Error";
import { SessionTimeoutManager } from "~/shared/components/SessionTimeoutManager";
import { SocketProvider } from "~/shared/components/SocketProvider";
import { authenticationGuardMiddleware } from "~/shared/guards/authentication.guard";
import { TEAM_EVENTS } from "~/team/constants/events.constants";
import { TEAM_EXPERIENCE_PREFIX } from "~/team/constants/paths.constants";
import { getTeamRoutes } from "~/team/team.routes";

function createPrefixMiddleware(queryClient: QueryClient, posthogClient?: PostHog): unstable_MiddlewareFunction {
  return async ({ request }, next) => {
    const user = await queryClient.ensureQueryData(USERS_QUERIES.profile());

    if (user.type === "breakline") {
      const url = new URL(request.url);
      const hasPrefix = url.pathname.startsWith(`/${TEAM_EXPERIENCE_PREFIX}`);

      if (!hasPrefix) {
        posthogClient?.capture(TEAM_EVENTS.TEAM_PREFIX_REDIRECT_CALLED, {
          requestPathname: url.pathname,
        });

        throw redirect(`/${TEAM_EXPERIENCE_PREFIX}${url.pathname === "/" ? "" : url.pathname}`);
      }
    }

    return next();
  };
}

export function PrivateRoutes(queryClient: QueryClient, posthogClient?: PostHog): RouteObject[] {
  return [
    {
      unstable_middleware: [authenticationGuardMiddleware(), createPrefixMiddleware(queryClient, posthogClient)],
      hydrateFallbackElement: <LoadingContainer level="full" />,
      errorElement: <TopLevelRouteErrorHandler />,
      element: (
        <React.Suspense fallback={<LoadingContainer level="full" />}>
          <SocketProvider>
            <Outlet />
            <SessionTimeoutManager />
          </SocketProvider>
        </React.Suspense>
      ),
      children: [...getTeamRoutes(queryClient, posthogClient), ...getParticipantRoutes(queryClient, posthogClient)],
    },
  ];
}
